import React from 'react';
import { ChevronDown, ChevronUp, Mail, CheckCircle2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

export const TestimonialsSection = ({ testimonials }) => (
  <section className="py-20">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-16">
        <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 mb-4">Testimonios</Badge>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Lo que Dicen Nuestros Usuarios</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.map((t) => (
          <Card key={t.name} className="bg-sky-900/20 border-sky-800/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-orange-500 flex items-center justify-center">
                  <span className="font-bold text-white">{t.avatar}</span>
                </div>
                <div>
                  <p className="font-medium text-white">{t.name}</p>
                  <p className="text-sky-300/50 text-sm">{t.location}</p>
                </div>
                <Badge className="ml-auto bg-orange-500/10 text-orange-400 border-orange-500/20">{t.plan}</Badge>
              </div>
              <p className="text-sky-100 italic">"{t.text}"</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

export const FAQSection = ({ faqs, openFaq, setOpenFaq }) => (
  <section id="faq" className="py-20 bg-gradient-to-b from-sky-900/20 to-transparent">
    <div className="max-w-3xl mx-auto px-4">
      <div className="text-center mb-16">
        <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 mb-4">FAQ</Badge>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Preguntas Frecuentes</h2>
      </div>
      <div className="space-y-4">
        {faqs.map((faq, idx) => (
          <div key={faq.question.slice(0, 30)} className="border border-sky-800/30 rounded-2xl overflow-hidden">
            <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-6 text-left hover:bg-sky-900/30 transition-colors">
              <span className="font-medium text-white pr-4">{faq.question}</span>
              {openFaq === idx ? <ChevronUp size={20} className="text-orange-400 flex-shrink-0" /> : <ChevronDown size={20} className="text-sky-400 flex-shrink-0" />}
            </button>
            {openFaq === idx && (
              <div className="px-6 pb-6">
                <p className="text-sky-200/70">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const CTASection = ({ onAction }) => (
  <section className="py-20">
    <div className="max-w-4xl mx-auto px-4">
      <Card className="bg-gradient-to-br from-sky-900/50 to-orange-900/30 border-sky-500/20 overflow-hidden">
        <CardContent className="p-12 text-center relative">
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Listo para Jugar Mas Inteligente?</h2>
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              {['Analisis en tiempo real', 'Sócrates incluido', 'Soporte 24/7'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-white">
                  <CheckCircle2 size={20} className="text-sky-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <Button onClick={onAction} size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-12 py-7 rounded-xl font-bold" data-testid="cta-btn">
              Crear Cuenta Gratis
              <ArrowRight className="ml-2" size={22} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </section>
);

export const FooterSection = () => (
  <footer className="py-16 border-t border-sky-800/30 bg-sky-950/30">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.png" alt="NumeroMaestro" className="h-12 w-auto" />
          </div>
          <p className="text-sky-200/60 mb-4 max-w-sm">Sistema inteligente de analisis con Sócrates para todas las loterias dominicanas.</p>
          <div className="flex gap-4">
            <a href="mailto:soporte@numeromaestro.com" aria-label="Escribir a soporte" className="w-10 h-10 rounded-full bg-sky-900/30 flex items-center justify-center text-sky-300/60 hover:text-white hover:bg-sky-800/50 transition-colors">
              <Mail size={18} />
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-bold text-white mb-4">Producto</h4>
          <ul className="space-y-2">
            <li><a href="#features" className="text-sky-200/60 hover:text-white transition-colors">Caracteristicas</a></li>
            <li><a href="#pricing" className="text-sky-200/60 hover:text-white transition-colors">Precios</a></li>
            <li><a href="#faq" className="text-sky-200/60 hover:text-white transition-colors">FAQ</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold text-white mb-4">Contacto</h4>
          <ul className="space-y-2">
            <li><a href="mailto:soporte@numeromaestro.com" className="text-sky-200/60 hover:text-white transition-colors">soporte@numeromaestro.com</a></li>
          </ul>
        </div>
      </div>
      <div className="pt-8 border-t border-sky-800/30">
        <p className="text-orange-400/80 text-sm mb-4 text-center">Las recomendaciones se basan en analisis estadistico y no garantizan resultados. Juegue responsablemente.</p>
        <p className="text-sky-300/50 text-sm text-center">&copy; 2026 NumeroMaestro. Todos los derechos reservados.</p>
      </div>
    </div>
  </footer>
);
